import React, { useEffect, useRef, useState } from "react";

/**
 * Decorative SVG roots that draw themselves once the section enters the viewport.
 * Pure CSS stroke-dashoffset animation, no library required.
 */
export default function RootsDraw({ className = "" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const cls = (n) => `eu-root-path${inView ? " eu-in-view" : ""}`;

  return (
    <svg
      ref={ref}
      viewBox="0 0 800 240"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      data-testid="historia-roots-anim"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="rootGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#9ead9b" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#b45f3e" stopOpacity="0.25" />
        </linearGradient>
      </defs>

      {/* Main trunk */}
      <path
        d="M400 0 C 400 60 380 100 360 140 C 345 170 330 200 300 240"
        stroke="url(#rootGrad)"
        strokeWidth="2.2"
        fill="none"
        className={cls()}
        style={{ "--len": 360 }}
      />
      {/* Mirror trunk */}
      <path
        d="M400 0 C 400 60 420 100 440 140 C 460 175 480 205 510 240"
        stroke="url(#rootGrad)"
        strokeWidth="2.2"
        fill="none"
        className={cls()}
        style={{ "--len": 360 }}
      />
      {/* Branch left 1 */}
      <path
        d="M360 140 C 320 150 280 165 230 180 C 195 188 160 198 120 215"
        stroke="url(#rootGrad)"
        strokeWidth="1.4"
        fill="none"
        className={cls()}
        style={{ "--len": 400 }}
      />
      {/* Branch left 2 */}
      <path
        d="M340 175 C 290 178 240 188 195 205"
        stroke="url(#rootGrad)"
        strokeWidth="1.1"
        fill="none"
        className={cls()}
        style={{ "--len": 220 }}
      />
      {/* Branch right 1 */}
      <path
        d="M440 140 C 480 152 525 168 580 184 C 620 195 660 205 700 220"
        stroke="url(#rootGrad)"
        strokeWidth="1.4"
        fill="none"
        className={cls()}
        style={{ "--len": 400 }}
      />
      {/* Branch right 2 */}
      <path
        d="M460 175 C 510 180 560 190 610 208"
        stroke="url(#rootGrad)"
        strokeWidth="1.1"
        fill="none"
        className={cls()}
        style={{ "--len": 220 }}
      />
      {/* Hair roots */}
      <path
        d="M300 240 C 290 230 285 220 282 208"
        stroke="url(#rootGrad)"
        strokeWidth="0.9"
        fill="none"
        className={cls()}
        style={{ "--len": 90 }}
      />
      <path
        d="M510 240 C 520 228 525 218 528 206"
        stroke="url(#rootGrad)"
        strokeWidth="0.9"
        fill="none"
        className={cls()}
        style={{ "--len": 90 }}
      />
      <path
        d="M195 205 C 188 198 184 192 180 184"
        stroke="url(#rootGrad)"
        strokeWidth="0.8"
        fill="none"
        className={cls()}
        style={{ "--len": 70 }}
      />
      <path
        d="M610 208 C 618 200 622 194 626 186"
        stroke="url(#rootGrad)"
        strokeWidth="0.8"
        fill="none"
        className={cls()}
        style={{ "--len": 70 }}
      />
    </svg>
  );
}
